import React, { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX, Ear, Square } from "lucide-react";
import { sfx, useSoundEnabled, speak, stopSpeaking, speechSupported } from "./nqSound";

export const shuffle = (arr) => { const a = [...arr]; for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; } return a; };
export const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
export const precisionOf = (hits, total, errors) => Math.max(0, Math.round((100 * (hits - errors * 0.5)) / Math.max(1, total)));
export const starsOf = (precision) => (precision >= 85 ? 3 : precision >= 60 ? 2 : 1);
export const PRACTICE_SECONDS = 10;
export const FLASH_MS = 550;

// Compte à rebours en secondes ; appelle onEnd une seule fois à 0.
export const useCountdown = (seconds, running, onEnd) => {
  const [left, setLeft] = useState(seconds);
  const ended = useRef(false);
  useEffect(() => { if (!running) return undefined; const id = setInterval(() => setLeft((l) => Math.max(0, l - 1)), 1000); return () => clearInterval(id); }, [running]);
  useEffect(() => { if (left === 0 && running && !ended.current) { ended.current = true; onEnd(); } }, [left, running, onEnd]);
  return left;
};

export const TimerBar = ({ left, total, color }) => (
  <div className="flex items-center gap-3" data-testid="nq-timer">
    <span className="text-lg">⏳</span>
    <div className="h-4 flex-1 rounded-full bg-white/70 overflow-hidden shadow-inner"><div className={`h-full rounded-full transition-all duration-1000 ease-linear ${left <= 5 ? "animate-pulse" : ""}`} style={{ width: `${(left / total) * 100}%`, background: left <= 5 ? "#E0705C" : color }} /></div>
    <span className={`text-base font-black tabular-nums w-12 text-right ${left <= 5 ? "text-[#E0705C]" : "text-[var(--ink)]"}`}>{left}s</span>
  </div>
);

export const Hud = ({ items }) => (
  <div className="flex flex-wrap gap-2 justify-center" data-testid="nq-hud">
    {items.map(([label, value, color]) => <span key={label} className="text-sm font-bold px-3.5 py-1.5 rounded-full text-white shadow-sm" style={{ background: color || "#B58C67" }}>{label} {value}</span>)}
  </div>
);

export const SoundToggle = ({ dark = false, testid = "nq-sound-toggle" }) => {
  const [on, setOn] = useSoundEnabled();
  return (
    <button onClick={() => { setOn(!on); if (!on) sfx.tick(); }} aria-label={on ? "Couper les sons" : "Activer les sons"} title={on ? "Sons activés" : "Sons coupés"} data-testid={testid} data-on={on ? "1" : "0"}
      className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full border transition-colors ${dark ? "bg-white/15 border-white/30 text-white hover:bg-white/25" : "bg-white border-[var(--border)] text-[var(--ink)] hover:bg-[var(--sand)]"}`}>
      {on ? <Volume2 size={14} /> : <VolumeX size={14} />} {on ? "Sons" : "Muet"}
    </button>
  );
};

// Feedback immédiat : anneau coloré + bulle animée « ✓ +1 » / « ✗ Piège ! » + son.
export const Flash = ({ kind, okLabel = "✓ +1", koLabel = "✗ Piège !" }) => {
  useEffect(() => { if (kind === "ok") sfx.ok(); else if (kind === "ko") sfx.ko(); }, [kind]);
  if (!kind) return null;
  return (
    <div className="pointer-events-none absolute inset-0 z-20 rounded-3xl" data-testid={`nq-flash-${kind}`}>
      <div className={`absolute inset-0 rounded-3xl ring-4 ${kind === "ok" ? "ring-[#7CC6A6]" : "ring-[#E0705C]"}`} />
      <div className="absolute left-1/2 top-4 -translate-x-1/2"><span className={`nq-pop inline-block px-4 py-1.5 rounded-full text-white text-lg font-black shadow-lg ${kind === "ok" ? "bg-[#2F7A55]" : "bg-[#B4483A]"}`}>{kind === "ok" ? okLabel : koLabel}</span></div>
    </div>
  );
};

const StepCard = ({ icon, label, text, color }) => (
  <div className="rounded-2xl bg-white p-4 text-left shadow-sm border-t-4" style={{ borderColor: color }}>
    <div className="text-2xl">{icon}</div>
    <div className="text-xs uppercase tracking-widest font-black mt-1" style={{ color }}>{label}</div>
    <p className="text-sm text-[var(--ink)] mt-1 leading-snug">{text}</p>
  </div>
);

const Chips = ({ items, ok }) => (
  <div className="flex flex-wrap justify-center gap-1.5">
    {items.map((it, i) => <span key={i} className={`inline-flex items-center justify-center min-w-[2.5rem] px-2.5 py-1 rounded-xl bg-white border-2 ${ok ? "border-[#7CC6A6]" : "border-[#E0705C] opacity-80"} ${String(it).length <= 3 ? "text-2xl" : "text-xs font-semibold text-[var(--ink)]"}`}>{it}</span>)}
  </div>
);

// Bouton « Écouter la consigne » : lit le but et les 3 étapes à voix haute.
export const SpeakButton = ({ text, color, testid = "nq-speak" }) => {
  const [speaking, setSpeaking] = useState(false);
  useEffect(() => () => stopSpeaking(), []);
  if (!speechSupported()) return null;
  const toggle = () => { if (speaking) { stopSpeaking(); setSpeaking(false); } else if (speak(text, () => setSpeaking(false))) setSpeaking(true); };
  return (
    <button onClick={toggle} data-testid={testid} data-speaking={speaking ? "1" : "0"} aria-label={speaking ? "Arrêter la lecture" : "Écouter la consigne"}
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold border-2 transition-all active:scale-95 ${speaking ? "text-white animate-pulse" : "bg-white hover:bg-[var(--sand)]"}`} style={speaking ? { background: color, borderColor: color } : { borderColor: color, color }}>
      {speaking ? <><Square size={14} /> Stop</> : <><Ear size={16} /> 🔈 Écouter la consigne</>}
    </button>
  );
};

const briefText = (brief) => `Ton but : ${brief.goal} Regarde : ${brief.regarde} Fais : ${brief.fais} Attention : ${brief.attention}`;

// Consigne enfant : but, 3 étapes (Regarde / Fais / Attention), aperçu cible vs pièges, lecture vocale, essai + départ.
export const Briefing = ({ power, level, brief, tried, onStart, onTry }) => (
  <div className="rounded-3xl border-2 p-5 sm:p-8 space-y-5 animate-fade-up" style={{ borderColor: power.color, background: `linear-gradient(160deg, ${power.color}22, #FFFFFF 55%)` }} data-testid="nq-briefing">
    <div className="text-center">
      <div className="text-6xl nq-float">{power.emoji}</div>
      <div className="text-xs uppercase tracking-[0.25em] font-bold mt-1" style={{ color: power.color }}>{power.worldEmoji} {power.world} · Niveau {level.n} — {level.name}</div>
      <h3 className="font-serif-title text-2xl sm:text-3xl text-[var(--ink)] mt-1">{brief.title}</h3>
    </div>
    <div className="rounded-2xl px-4 py-3 text-center text-white font-bold text-base sm:text-lg shadow-sm" style={{ background: power.color }} data-testid="nq-brief-goal">🎯 Ton but : {brief.goal}</div>
    <div className="flex justify-center"><SpeakButton text={briefText(brief)} color={power.color} /></div>
    <div className="grid sm:grid-cols-3 gap-3" data-testid="nq-brief-steps">
      <StepCard icon="👀" label="Regarde" text={brief.regarde} color="#5E9BD6" />
      <StepCard icon="👆" label="Fais" text={brief.fais} color="#7CC6A6" />
      <StepCard icon="⚠️" label="Attention" text={brief.attention} color="#E0705C" />
    </div>
    {brief.preview && (
      <div className="grid grid-cols-2 gap-3" data-testid="nq-brief-preview">
        <div className="rounded-2xl bg-[#E4F5EC] p-3"><div className="text-xs font-black text-[#2F7A55] text-center mb-2">✅ {brief.preview.okLabel || "Ta cible"}</div><Chips items={brief.preview.ok} ok /></div>
        <div className="rounded-2xl bg-[#FDE4DF] p-3"><div className="text-xs font-black text-[#B4483A] text-center mb-2">❌ {brief.preview.koLabel || "Les pièges"}</div>{brief.preview.ko.length ? <Chips items={brief.preview.ko} /> : <p className="text-xs text-center text-[var(--ink-soft)]">Aucun piège à ce niveau.</p>}</div>
      </div>
    )}
    <div className="flex flex-col sm:flex-row items-center justify-center gap-2 text-xs text-center text-[var(--ink-soft)]"><span>👨‍👩‍👧 Le parent lit la consigne à voix haute, puis l'enfant explique la règle avec ses mots.</span><SoundToggle testid="nq-sound-toggle-brief" /></div>
    {tried && <div className="rounded-2xl bg-[#FFF3D6] border border-[#F2B84B] px-4 py-2.5 text-center text-sm font-bold text-[#B8781A] animate-fade-up" data-testid="nq-tried">🧪 Essai terminé ! Prêt pour la vraie mission ?</div>}
    <div className="flex flex-col sm:flex-row justify-center gap-3">
      <button onClick={onTry} data-testid="nq-try-game" className="px-6 py-3 rounded-full bg-white border-2 font-bold text-base hover:bg-[var(--sand)] active:scale-95 transition-all" style={{ borderColor: power.color, color: power.color }}>🧪 Essai de {PRACTICE_SECONDS} s (sans score)</button>
      <button onClick={onStart} data-testid="nq-start-game" className="px-8 py-3.5 rounded-full text-white font-bold text-lg shadow-md hover:brightness-105 active:scale-95 transition-all" style={{ background: power.color }}>🚀 C'est parti !</button>
    </div>
  </div>
);

export const BigButton = ({ children, onClick, color, testid, disabled }) => (
  <button onClick={onClick} disabled={disabled} data-testid={testid}
    className="px-10 py-5 rounded-3xl text-white text-xl font-black shadow-lg active:scale-95 hover:brightness-105 transition-all disabled:opacity-40 select-none"
    style={{ background: color }}>{children}</button>
);

// Compte à rebours 3-2-1-GO avant le départ.
export const Countdown = ({ color, onDone, label }) => {
  const [n, setN] = useState(3);
  useEffect(() => { if (n < 0) { onDone(); return undefined; } if (n > 0) sfx.tick(); else sfx.go(); const id = setTimeout(() => setN(n - 1), 950); return () => clearTimeout(id); }, [n, onDone]);
  return (
    <div className="rounded-3xl min-h-[280px] flex flex-col items-center justify-center text-white text-center p-8" style={{ background: color }} data-testid="nq-countdown">
      {label && <div className="text-sm font-bold uppercase tracking-widest opacity-80 mb-2">{label}</div>}
      <div key={n} className="nq-count text-8xl font-black drop-shadow-lg" data-testid="nq-countdown-value">{n > 0 ? n : "GO !"}</div>
      <div className="text-sm opacity-80 mt-3">Prépare tes doigts…</div>
    </div>
  );
};

// Écran « Mission terminée ! » animé avant le débrief.
export const EndScreen = ({ power, result, onContinue }) => {
  useEffect(() => { sfx.fanfare(); const id = setTimeout(onContinue, 2600); return () => clearTimeout(id); }, [onContinue]);
  return (
    <div className="rounded-3xl min-h-[300px] flex flex-col items-center justify-center text-center p-8 text-white animate-fade-up" style={{ background: `linear-gradient(160deg, ${power.color}, #1F2A44)` }} data-testid="nq-end-screen">
      <div className="text-6xl nq-float">{power.emoji}</div>
      <h3 className="font-serif-title text-3xl sm:text-4xl mt-3">Mission terminée !</h3>
      <div className="text-5xl mt-3 tracking-wider" data-testid="nq-end-stars">{[1, 2, 3].map((k) => <span key={k} className="nq-star" style={{ animationDelay: `${0.3 + k * 0.35}s`, opacity: k <= result.stars ? 1 : 0.25 }}>⭐</span>)}</div>
      <p className="text-sm opacity-90 mt-3">{result.detail}</p>
      <button onClick={onContinue} data-testid="nq-end-continue" className="mt-5 px-6 py-2.5 rounded-full bg-white/20 hover:bg-white/30 text-sm font-bold">Voir le débriefing →</button>
    </div>
  );
};

// Fond immersif par monde autour de la zone de jeu.
export const Arena = ({ power, practice, children, onStop }) => (
  <div className="rounded-3xl p-3 sm:p-5 space-y-3 border-2" style={{ borderColor: power.color, background: `linear-gradient(180deg, ${power.color}2E, ${power.color}0F 40%, #FFFFFF)` }} data-testid={practice ? "nq-practice" : "nq-arena"}>
    <div className="flex items-center justify-between gap-2 text-xs font-bold px-1">
      <span style={{ color: power.color }}>{power.worldEmoji} {power.world}</span>
      {practice
        ? <span className="inline-flex items-center gap-2"><span className="px-3 py-1 rounded-full bg-[#F2B84B] text-[#1F2A44]">🧪 Essai — pas de score</span><button onClick={onStop} data-testid="nq-practice-stop" className="px-3 py-1 rounded-full bg-white border border-[var(--border)] text-[var(--ink)]">Passer à la vraie mission</button></span>
        : <span className="inline-flex items-center gap-2"><span className="px-3 py-1 rounded-full bg-white/80 text-[var(--ink)]">{power.emoji} {power.name}</span><SoundToggle testid="nq-sound-toggle-arena" /></span>}
    </div>
    {children}
  </div>
);
